import { useForm, FieldData, TextField, Icon, Box, withStyles, PortalResetPasswordRequest } from 'vopay';
import { useMemo, useState } from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Button, Title, PasswordValidationWidget } from 'UI';
import styles from '../Login.module.scss';

const cn = withStyles(styles);

export type ResetPasswordFormValues = Pick<PortalResetPasswordRequest, 'Password'>;

type ResetPasswordFormProps = {
  onSubmit: (values: ResetPasswordFormValues) => Promise<void>;
  errorMessage: string;
};

export default function ResetPasswordForm({ onSubmit, errorMessage }: ResetPasswordFormProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Login.ResetPassword.Page' });
  const { t: tLogin } = useTranslation('translations', { keyPrefix: 'Login.Form' });
  const [hasValidPassword, setHasValidPassword] = useState(false);

  const passwordLabel = tLogin('passwordLabel', { defaultValue: 'Password' });

  const config = useMemo<FieldData<ResetPasswordFormValues>[]>(
    () => [
      {
        fieldName: 'Password',
        getInputState: () => 'required',
        showField: () => true,
        validate: () => Yup.string().label(passwordLabel).required(),
        label: () => passwordLabel,
        renderField: ({ getFormikData }) => (
          <TextField
            {...getFormikData<ResetPasswordFormValues['Password']>({})}
            preset="password"
            variant="outlineShadow"
            placeholder={t('inputPlaceholder', { defaultValue: 'Password' })}
            elementLeft={<Icon icon="lock" size="md" color="medium-dark" />}
          />
        ),
      },
    ],
    [passwordLabel, t],
  );

  const { ctx, renderFormElements } = useForm<ResetPasswordFormValues>({
    initialValues: { Password: '' },
    config,
    onSubmit,
  });

  return (
    <Box flx="col" width="100%">
      <Box mb="sm" flx="col" ai="center" height="auto">
        <Title level="4">{t('title', { defaultValue: 'Reset Password' })}</Title>
      </Box>

      <form onSubmit={ctx.handleSubmit}>
        <Box mb="sm" flx="col" height="auto">
          {renderFormElements({
            config,
            // Render label + input ourselves so the field matches the login form's markup and
            // styling (no ElementContainer chrome or required asterisk) in the shared card.
            override: ({ label, template }) => (
              <>
                <div className={cn('fieldLabel')}>{label}</div>
                {template}
              </>
            ),
          })}
        </Box>

        <Box jc="fe" flx="col" height="auto" pb="md">
          <PasswordValidationWidget password={ctx.values.Password} onValidate={setHasValidPassword} />
        </Box>

        {errorMessage && <div className={cn('errorMSG', 'submitError')}>{errorMessage}</div>}

        <Box jc="fe" flx="col" height="auto">
          <Button
            id="reset-password-submit"
            type="submit"
            colorScheme="primary"
            size="large"
            className={cn('submitButton')}
            // The widget above lists which strength rules are still unmet; submit unlocks once they all pass.
            disabled={ctx.isSubmitting || !hasValidPassword}
          >
            {t('submitButton', { defaultValue: 'Reset Password' })}
          </Button>
        </Box>
      </form>
    </Box>
  );
}
